/**
 * Quick Add to Cart Functionality
 * Historical Emporium Theme
 * 
 * Handles adding single-variant products to cart without page navigation
 */

class QuickAdd {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.attachEventListeners());
    } else {
      this.attachEventListeners();
    }
  }

  attachEventListeners() {
    // Use event delegation for better performance
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.js-quick-add-btn');
      if (button && !button.disabled) {
        // Prevent link navigation
        e.preventDefault();
        e.stopPropagation();
        
        this.handleQuickAdd(button);
      }
    });
  }

  async handleQuickAdd(button) {
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    const productUrl = button.dataset.productUrl;

    if (!variantId) {
      console.error('Quick Add: No variant ID found');
      return;
    }

    // Store original button content
    const originalContent = button.innerHTML;

    // Set loading state
    this.setLoadingState(button, true);

    try {
      // Add to cart via Shopify AJAX Cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Success!
        this.setSuccessState(button);
        this.updateCartCount();
        
        // Dispatch custom event for cart updates
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { item: data }
        }));

        // Redirect to cart page after brief delay (show success feedback first)
        setTimeout(() => {
          window.location.href = '/cart';
        }, 800);
      } else {
        // Handle Shopify errors (out of stock, etc)
        throw new Error(data.description || data.message || 'Unable to add to cart');
      }
    } catch (error) {
      console.error('Quick Add Error:', error);
      this.setErrorState(button, error.message);
      
      // Reset button after 3 seconds
      setTimeout(() => {
        this.resetButton(button, originalContent);
      }, 3000);
    }
  }

  setLoadingState(button, isLoading) {
    button.disabled = isLoading;
    button.classList.toggle('is-loading', isLoading);
    
    const btnText = button.querySelector('.btn-text');
    const spinner = button.querySelector('.loading-spinner');
    const icon = button.querySelector('.fa-shopping-cart');
    
    if (btnText) btnText.style.display = isLoading ? 'none' : '';
    if (spinner) spinner.style.display = isLoading ? 'inline-block' : 'none';
    if (icon) icon.style.display = isLoading ? 'none' : '';
  }

  setSuccessState(button) {
    button.classList.remove('is-loading');
    button.classList.add('is-success');
    button.disabled = false;
    
    const btnText = button.querySelector('.btn-text');
    const icon = button.querySelector('.fa-shopping-cart');
    const spinner = button.querySelector('.loading-spinner');
    
    if (spinner) spinner.style.display = 'none';
    if (icon) {
      icon.className = 'fas fa-check';
      icon.style.display = '';
    }
    if (btnText) {
      btnText.textContent = 'Added!';
      btnText.style.display = '';
    }

    // Announce to screen readers
    this.announceToScreenReader('Product added to cart');
  }

  setErrorState(button, errorMessage) {
    button.classList.remove('is-loading');
    button.classList.add('is-error');
    button.disabled = false;
    
    const btnText = button.querySelector('.btn-text');
    const icon = button.querySelector('.fa-shopping-cart');
    const spinner = button.querySelector('.loading-spinner');
    
    if (spinner) spinner.style.display = 'none';
    if (icon) {
      icon.className = 'fas fa-exclamation-circle';
      icon.style.display = '';
    }
    if (btnText) {
      btnText.textContent = 'Error';
      btnText.style.display = '';
    }

    // Announce error to screen readers
    this.announceToScreenReader(errorMessage || 'Unable to add to cart');
  }

  resetButton(button, originalContent) {
    button.innerHTML = originalContent;
    button.disabled = false;
    button.classList.remove('is-loading', 'is-success', 'is-error');
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const cart = await response.json();
        const count = cart.item_count;

        // Update all cart count elements
        document.querySelectorAll('[data-cart-count], .cart-count').forEach(element => {
          element.textContent = count;
          element.classList.toggle('cart-count--visible', count > 0);
        });

        // Update cart icon badge if exists
        const cartBadge = document.querySelector('.cart-count-bubble');
        if (cartBadge) {
          cartBadge.textContent = count;
          cartBadge.style.display = count > 0 ? '' : 'none';
        }
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  announceToScreenReader(message) {
    // Create or use existing aria-live region
    let liveRegion = document.getElementById('quick-add-aria-live');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'quick-add-aria-live';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = message;

    // Clear after announcement
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 1000);
  }
}

// Initialize Quick Add
new QuickAdd();
